<template>
    <div v-if="visible" class="timeline" :class="{ 'timeline--embedded': embedded }">
        <div
            v-for="(step, idx) in displaySteps"
            :key="step.id"
            class="timeline-step"
            :class="[
                `timeline-step--${step.status}`,
                { 'timeline-step--last': idx === displaySteps.length - 1 },
            ]"
        >
            <div class="timeline-rail" aria-hidden="true">
                <span class="timeline-dot" />
                <span v-if="idx < displaySteps.length - 1" class="timeline-line" />
            </div>

            <div class="timeline-body">
                <div class="timeline-head">
                    <span class="timeline-tag">{{ step.tag }}</span>
                    <span class="timeline-title">{{ step.title }}</span>
                    <span
                        v-if="step.status === 'active'"
                        class="timeline-pulse"
                        aria-hidden="true"
                    />
                </div>

                <p v-if="step.message" class="timeline-message">{{ step.message }}</p>

                <div
                    v-if="step.kind === 'sources' && step.sources?.length"
                    class="timeline-sources"
                >
                    <div
                        v-for="group in groupSources(step.sources)"
                        :key="group.key"
                        class="source-group"
                    >
                        <button
                            type="button"
                            class="source-group-head"
                            :aria-expanded="isOpen(group.key)"
                            @click="toggle(group.key)"
                        >
                            <ChatIcon
                                name="arrow-down"
                                :size="12"
                                class="source-caret"
                                :class="{ 'source-caret--open': isOpen(group.key) }"
                            />
                            <span class="source-name">{{ group.title }}</span>
                            <span class="source-meta">
                                {{ group.items.length }}
                                {{ groupCountLabel(group) }}
                                <template v-if="group.totalChunks">
                                    · {{ group.totalChunks }}
                                    {{ pluralize(group.totalChunks, ['фрагмент', 'фрагмента', 'фрагментов']) }}
                                </template>
                                <template v-else-if="group.totalRecords">
                                    · {{ formatRecords(group.totalRecords) }}
                                </template>
                            </span>
                        </button>
                        <ul v-if="isOpen(group.key)" class="source-items">
                            <li
                                v-for="(item, itemIdx) in group.items"
                                :key="`${group.key}-${itemIdx}`"
                            >
                                <a
                                    v-if="item.url"
                                    class="source-link"
                                    :href="item.url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    :title="item.url"
                                >
                                    <span class="source-link-title">{{ item.title || item.period || item.url }}</span>
                                    <span class="source-link-host">{{ formatHost(item.url) }}</span>
                                </a>
                                <template v-else>
                                    <!--
                                        Для items без URL (RAG, БД-источники):
                                        title — имя файла / название источника,
                                        period — короткая мета («N фрагментов»).
                                        Раньше тут рендерился только period — и
                                        пользователь видел «chunk #17/#13/…», не зная,
                                        из каких файлов это вообще.
                                    -->
                                    <span class="source-link-title">{{ item.title || item.period || 'без описания' }}</span>
                                    <span
                                        v-if="item.title && item.period"
                                        class="source-link-host"
                                    >{{ item.period }}</span>
                                    <span v-if="item.records" class="source-rec">{{ item.records }}</span>
                                </template>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'

const props = defineProps({
    sources: { type: Array, default: () => [] },
    steps: { type: Array, default: () => [] },
    streaming: { type: Boolean, default: false },
    /** Без собственной колонки точек — ветка answer-shell слева */
    embedded: { type: Boolean, default: false },
})

const openKeys = ref(new Set())

const sourceStep = computed(() => {
    if (!props.sources?.length) return null
    // Количество УНИКАЛЬНЫХ источников (после groupSources), а не сырых чанков:
    // веб-поиск присылает по одному чанку на каждую ссылку, и без группировки
    // тут выводилось бы что-то вроде «ИСТОЧНИКИ» при единственном веб-поиске.
    const groupsCount = groupSources(props.sources).length
    const tag = groupsCount > 1 ? 'ИСТОЧНИКИ' : 'ИСТОЧНИК'
    // Заголовок прежней формулировки «Подключение данных» воспринимался как
    // «AI пытается подключиться к данным и пока не подключился». Меняем на
    // нейтральную констатацию факта; на этапе стриминга — мягкое «…подбираю».
    const title = props.streaming
        ? 'Подбираю источники…'
        : groupsCount > 1
            ? 'Использованные источники'
            : 'Использованный источник'
    return {
        id: 'sources',
        kind: 'sources',
        tag,
        title,
        message: '',
        sources: props.sources,
        status: props.streaming ? 'active' : 'done',
    }
})

const displaySteps = computed(() => {
    const list = []
    if (sourceStep.value) list.push(sourceStep.value)
    return list
})

const visible = computed(() => displaySteps.value.length > 0)

function groupSources(sources) {
    const map = new Map()
    for (const src of sources || []) {
        if (!src) continue
        const key = src.code || src.type || 'source'
        const title = src.type || src.code || 'Источник'
        if (!map.has(key)) {
            map.set(key, {
                key,
                title,
                items: [],
                totalRecords: 0,
                totalChunks: 0,
                hasLinks: false,
                hasChunks: false,
            })
        }
        const group = map.get(key)
        group.items.push(src)
        if (src.url) group.hasLinks = true
        const num = parseRecords(src.records)
        if (num != null) group.totalRecords += num
        // RAG-источник присылает chunksCount (число фрагментов в файле) и
        // chunkIndexes (массив индексов чанков). Используем chunksCount, чтобы
        // показать суммарный вклад источника в шапке группы.
        const chunks = Number(src.chunksCount)
        if (Number.isFinite(chunks) && chunks > 0) {
            group.totalChunks += chunks
            group.hasChunks = true
        }
    }
    return Array.from(map.values())
}

/**
 * Лейбл для счётчика items в шапке группы.
 *   * RAG (есть chunksCount у items) → «N файлов» (после backend-агрегации
 *     items группы = уникальные файлы).
 *   * Веб-поиск (есть URL) → «N ссылок».
 *   * Прочее (БД-источники без URL) → «N запросов» (как раньше).
 */
function groupCountLabel(group) {
    const n = group.items.length
    if (group.hasChunks) return pluralize(n, ['файл', 'файла', 'файлов'])
    if (group.hasLinks) return pluralize(n, ['ссылка', 'ссылки', 'ссылок'])
    return pluralize(n, ['запрос', 'запроса', 'запросов'])
}

/**
 * Извлекает host из URL для компактного отображения справа от заголовка
 * ссылки. Невалидные URL глотаем — UI не должен ломаться из-за мусора в
 * данных.
 */
function formatHost(url) {
    if (!url) return ''
    try {
        const u = new URL(url)
        return u.host.replace(/^www\./, '')
    } catch (e) {
        return ''
    }
}

function toggle(key) {
    const next = new Set(openKeys.value)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    openKeys.value = next
}

function isOpen(key) {
    return openKeys.value.has(key)
}

function parseRecords(text) {
    if (!text) return null
    const match = String(text).match(/(\d[\d\s]*)/)
    if (!match) return null
    const n = Number(match[1].replace(/\s+/g, ''))
    return Number.isFinite(n) ? n : null
}

function formatRecords(total) {
    if (!Number.isFinite(total) || total <= 0) return ''
    return `${total.toLocaleString('ru-RU')} ${pluralize(total, ['запись', 'записи', 'записей'])}`
}

function pluralize(n, forms) {
    const abs = Math.abs(n) % 100
    const last = abs % 10
    if (abs > 10 && abs < 20) return forms[2]
    if (last > 1 && last < 5) return forms[1]
    if (last === 1) return forms[0]
    return forms[2]
}
</script>

<style scoped>
.timeline {
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-bottom: 4px;
}

.timeline--embedded {
    margin-bottom: 0;
}

.timeline--embedded .timeline-rail {
    display: none;
}

.timeline--embedded .timeline-step {
    grid-template-columns: 1fr;
    gap: 0;
    padding-bottom: 12px;
}

.timeline--embedded .timeline-step--last {
    padding-bottom: 0;
}

.timeline-step {
    display: grid;
    grid-template-columns: 20px 1fr;
    gap: 0 12px;
    padding-bottom: 14px;
}

.timeline-step--last {
    padding-bottom: 0;
}

.timeline-rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 4px;
}

.timeline-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #c0c0c0;
    flex-shrink: 0;
    z-index: 1;
    transition: background 0.2s ease, box-shadow 0.2s ease;
}

.timeline-step--active .timeline-dot {
    background: #23282C;
    box-shadow: 0 0 0 3px rgba(35, 40, 44, 0.12);
}

.timeline-step--done .timeline-dot {
    background: #23282C;
}

.timeline-line {
    flex: 1;
    width: 1px;
    min-height: 12px;
    margin-top: 4px;
    background: #e0e0e0;
}

.timeline-body {
    min-width: 0;
    padding-top: 0;
}

.timeline-head {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.timeline-tag {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #888888;
    padding: 3px 7px;
    background: #f0f0f0;
    border-radius: 999px;
}

.timeline-step--active .timeline-tag {
    background: #23282C;
    color: #fff;
}

.timeline-title {
    font-size: 12px;
    font-weight: 600;
    color: #23282C;
}

.timeline-pulse {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #198FA0;
    animation: timeline-pulse 1.2s ease-in-out infinite;
}

@keyframes timeline-pulse {
    0%, 100% { opacity: 0.35; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.35); }
}

.timeline-message {
    margin: 6px 0 0;
    font-size: 12px;
    line-height: 1.45;
    color: #5f5f5f;
}

.timeline-sources {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.source-group {
    border-radius: 10px;
    background: #f0f0f0;
    overflow: hidden;
}

.source-group-head {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 10px;
    border: none;
    background: transparent;
    font: inherit;
    font-size: 11px;
    color: #5f5f5f;
    cursor: pointer;
    text-align: left;
}

.source-group-head:hover {
    background: rgba(255, 255, 255, 0.5);
}

.source-caret {
    flex-shrink: 0;
    color: #888888;
    transition: transform 0.15s ease;
}

.source-caret--open {
    transform: rotate(180deg);
}

.source-name {
    font-weight: 600;
    color: #23282C;
}

.source-meta {
    color: #888888;
    margin-left: auto;
    white-space: nowrap;
}

.source-items {
    margin: 0;
    padding: 0 12px 8px 28px;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.source-items li {
    font-size: 11px;
    color: #5f5f5f;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.source-rec {
    color: #888888;
}

.source-rec::before {
    content: '·';
    margin-right: 4px;
    color: #c0c0c0;
}

.source-link {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    color: inherit;
    text-decoration: none;
    padding: 4px 6px;
    margin: -4px -6px;
    border-radius: 6px;
    transition: background-color 0.12s ease;
}

.source-link:hover {
    background: rgba(255, 255, 255, 0.6);
}

.source-link-title {
    color: #23282C;
    flex: 1;
    min-width: 0;
    word-break: break-word;
}

.source-link:hover .source-link-title {
    text-decoration: underline;
}

.source-link-host {
    color: #888888;
    font-size: 10.5px;
    margin-left: auto;
    flex-shrink: 0;
    white-space: nowrap;
}
</style>
